<template>
  <div class="form">
    <div class="form__content">
      <h1 
        class="extra-large-headline" 
        data-cy="confirm-detail-heading">
          {{ $t('ConfirmDetailForm.label.headline') }}
      </h1>
      <p 
        class="bodycopy" 
        data-cy="confirm-detail-body">
          {{ $t('ConfirmDetailForm.label.body') }}
      </p>
    </div>
      <TwoColumnTable 
        :tableRows="tableRows" 
        :isLoading="isLoading"
      />
      <ButtonSecondary
        data-cy="button-edit"
        @onButtonClick="onClickEdit"
      >
        {{ $t('ButtonSecondary.label.editDetails') }}
      </ButtonSecondary>
      <ButtonSecondary
        data-cy="button-update"
        @onButtonClick="onClickUpdate"
      >
        {{ $t('ButtonSecondary.label.correctDetails') }}
      </ButtonSecondary>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from 'vue-i18n';

import useApiHelpers from "@/composables/api/helpers";
import useCustomerActions from "@/composables/api/useCustomerActions";
import useGlobalCountriesActions from "@/composables/store/global/useGlobalCountriesActions";
import useSettingsEndpoints from "@/composables/api/useSettingsEndpoints";
import useProgressBarActions from "@/composables/progress/useProgressBarActions";
import { useIdentityQueueStore } from "@/stores/identityQueueStore";

import ButtonSecondary from "@/components/ui/buttons/ButtonSecondary.vue";
import TwoColumnTable from "@/components/ui/tables/TwoColumnTable.vue";

const router = useRouter();
const route = useRoute();

const { t } = useI18n();
const { postRequest } = useApiHelpers();
const { fetchCustomerId, fetchCustomerDetails } = useCustomerActions();
const { fetchCountries } = useGlobalCountriesActions();
const { updateToPending } = useProgressBarActions();
const { getRestrictedUSStatesSettings } = useSettingsEndpoints();
const identityQueueStore = useIdentityQueueStore();

const isLoading = ref(true);
const tableRows = ref({
    fullName: {
        label: t('TwoColumnTable.label.fullName'),
        value: '',
    },
    address: {
      label: t('TwoColumnTable.label.address'),
      value: '',
    },
    postcode: {
      label: t('TwoColumnTable.label.postCode'),
      value: '',
    },
    country: {
      label: t('TwoColumnTable.label.country'),
      value: '',
    }
    
});
const customerInfo = ref([]);

const onClickEdit = () => {
  router.push({name: 'identity.update-details'})
}

const onClickUpdate = async() => {
  postRequest("/customer/update-details", {
    personalDetailsUnchanged: true,
  }, async() => {
    identityQueueStore.updateToNextRoute();
    identityQueueStore
        .fetchNextIdentityRoute(route)
        .then((nextRoute) => {
          router.push(nextRoute);
        })
  });
}

const setCustomerData = (customer) => {
  const customerFields = ['fullName', 'postcode', 'address', 'country'];
  customerFields.forEach(field => {

    tableRows.value[field].value = customer[field];

    if (field === 'fullName') {
      const middleName = customer.middleName ? customer.middleName : ''
      tableRows.value[field].value = `${customer.givenName} ${middleName} ${customer.surname}`;
    } 

    if (field === 'address') {
      const state = customer.stateName ? customer.stateName : customer.state;
      tableRows.value[field].value = `${customer.addressLine1} ${customer.suburb} ${state}`;
    }

    if (field === 'country') {
      tableRows.value[field].value = `${customer.countryName}`;
    }
  });
}


onMounted(async() => {

  updateToPending("identity");

  isLoading.value = true;
  const countries = await fetchCountries();
  const customerDetails = await fetchCustomerDetails();

  customerInfo.value = customerDetails;
  if (customerDetails && countries) {
    if (customerDetails.country === 'US') {
      const { data: { acceptedStates, restrictedStates } } = await getRestrictedUSStatesSettings();
      const states = {...acceptedStates, ...restrictedStates};
      customerDetails.stateName = states[customerDetails.state];
    }
    
    const country = countries.find(country => country.code === customerDetails.country);
    customerDetails.countryName = country.name;
    setCustomerData(customerDetails);
  }

  isLoading.value = false;
})

</script>
<style lang="scss" scoped>
  .buttonSecondary {
    margin-bottom: 1rem;
  }
</style>
